import {
  Children,
  Fragment,
  isValidElement,
  type ReactElement,
  type ReactNode,
} from 'react';

import type { PressEvent } from '../../primitives/Pressable';
import type { BottomSheetAlign, BottomSheetFooterLayout } from './BottomSheet.types';

/**
 * Composable slots for {@link BottomSheetSurface} — the Figma "Bottom sheet"
 * anatomy (file `YZK9r3wDhvw1pY60KlVEIj`, frame **5456:48693**) expressed as
 * components instead of props:
 *
 * ```
 * [handle]                                    showHandle
 * [header]
 *    ├ back button  40px                      <BottomSheetBack>
 *    ├ Image Header 242px, r28                <BottomSheetImage>
 *    ├ Title text   H2 20/28 semibold         <BottomSheetTitle>
 *    ├ Sub text     14/20 medium              <BottomSheetDescription>
 *    └ close ✕      36px                      <BottomSheetClose>
 * [Content / Media Slot]                      <BottomSheetContent>, or
 *                                             anything else in `children`
 * [Pagination Indicator]                      pagination prop
 * [footer]                                    <BottomSheetActions>
 * ```
 *
 * ```tsx
 * <BottomSheet open={open} onOpenChange={setOpen}>
 *   <BottomSheetHeader>
 *     <BottomSheetTitle>Delete this deck?</BottomSheetTitle>
 *     <BottomSheetDescription>All 248 cards go with it.</BottomSheetDescription>
 *     <BottomSheetClose />
 *   </BottomSheetHeader>
 *   <DeckPreview deck={deck} />
 *   <BottomSheetActions layout="inline">
 *     <Button variant="secondary" onPress={dismiss}>Cancel</Button>
 *     <Button variant="alert" onPress={remove}>Delete</Button>
 *   </BottomSheetActions>
 * </BottomSheet>
 * ```
 *
 * ## They are markers, not renderers
 *
 * Every component here returns `null`; the sheet reads its props and draws the
 * region itself. Two reasons, and both are load-bearing:
 *
 * 1. **The footer is not in the card's tree on native.** gorhom renders it
 *    through its own `footerComponent` slot so it stays pinned while the body
 *    scrolls and rides above the keyboard. An in-place `<BottomSheetActions>`
 *    could not be there, so the sheet has to HOIST it.
 * 2. **The design can't drift.** There is exactly one place that knows what a
 *    sheet title, a back button or a footer row looks like, and it is the same
 *    code the `title` / `onBack` / `primaryAction` props go through. A slot
 *    can't render a 17px title by accident because it never renders anything.
 *
 * The practical consequence: slots must be direct children of the sheet (or of
 * `<BottomSheetHeader>`). A slot wrapped in your own component or a fragment is
 * invisible to the split and falls through to the body. Conditionals are fine —
 * `{canGoBack && <BottomSheetBack onPress={back} />}`.
 *
 * ## Slots and props are alternatives
 *
 * The prop form (`title`, `subtitle`, `primaryAction`, …) stays: for the common
 * "title + one CTA" sheet it is shorter and says the same thing. Reach for slots
 * when the content is richer than a prop can carry — a title with an inline
 * badge, a footer that isn't one or two buttons, an image header. When both are
 * supplied for the same region **the slot wins**.
 */

type SlotName =
  | 'header'
  | 'title'
  | 'description'
  | 'image'
  | 'back'
  | 'close'
  | 'content'
  | 'actions';

/** Static tag the split reads off a component. */
interface SlotMarked {
  bottomSheetSlot?: SlotName;
}

function slotOf(node: ReactNode): SlotName | undefined {
  if (!isValidElement(node)) return undefined;
  const type = node.type;
  if (typeof type !== 'function' && typeof type !== 'object') return undefined;
  return (type as SlotMarked).bottomSheetSlot;
}

// ─── Slots ──────────────────────────────────────────────────────────────────

export interface BottomSheetHeaderProps {
  /** Title-group alignment (Figma `Alignement`). Default `'left'`. */
  align?: BottomSheetAlign;
  /** Tighter spacing under the handle — for bodies that start immediately. */
  compact?: boolean;
  /** Hairline under the header. Use when the body scrolls beneath it. */
  showBorder?: boolean;
  children?: ReactNode;
}

/**
 * Groups the header slots and carries the header-level options. Optional: the
 * header slots are also picked up as direct children of the sheet, so this is
 * for grouping and for `align` / `compact` / `showBorder`.
 */
export function BottomSheetHeader(_props: BottomSheetHeaderProps): null {
  return null;
}
BottomSheetHeader.displayName = 'BottomSheetHeader';
BottomSheetHeader.bottomSheetSlot = 'header' as const;

export interface BottomSheetTitleProps {
  children?: ReactNode;
  /** Extra classes on the title text. The 20/28 semibold type stays. */
  className?: string;
}

/** Figma "Title text" — Mona Sans SemiBold 20/28, `secondary-800`. */
export function BottomSheetTitle(_props: BottomSheetTitleProps): null {
  return null;
}
BottomSheetTitle.displayName = 'BottomSheetTitle';
BottomSheetTitle.bottomSheetSlot = 'title' as const;

export interface BottomSheetDescriptionProps {
  children?: ReactNode;
  /** Extra classes on the sub text. The 14/20 medium type stays. */
  className?: string;
}

/** Figma "Sub text" — Mona Sans Medium 14/20, `secondary-500`. */
export function BottomSheetDescription(_props: BottomSheetDescriptionProps): null {
  return null;
}
BottomSheetDescription.displayName = 'BottomSheetDescription';
BottomSheetDescription.bottomSheetSlot = 'description' as const;

export interface BottomSheetImageProps {
  /** The `<Image>` / `<img>` to fill the frame. */
  children?: ReactNode;
  /**
   * Frame height in px. Figma "Image Header" = 242. `'auto'` lets the frame
   * hug a composed column instead (artwork + caption text inside the media
   * card).
   */
  height?: number | 'auto';
}

/**
 * Figma "Image Header" — a 28px-rounded, clipped frame above the title, with
 * the ✕ floated over the artwork. Selects the `Header +image` variant.
 */
export function BottomSheetImage(_props: BottomSheetImageProps): null {
  return null;
}
BottomSheetImage.displayName = 'BottomSheetImage';
BottomSheetImage.bottomSheetSlot = 'image' as const;

export interface BottomSheetBackProps {
  onPress?: (event?: PressEvent) => void;
  /** Default `'Back'`. */
  accessibilityLabel?: string;
  /**
   * Replace the 40px bevelled arrow entirely — for a leading affordance that
   * isn't "go back" (a filter reset, an avatar). The header still lays out as
   * the `Header +back button` variant.
   */
  children?: ReactNode;
}

/**
 * Figma's leading back button — Button `secondary` `m`, icon-only, 40px.
 * Its presence selects the `Header +back button` variant; wire it to the
 * previous entry of a `steps` sheet.
 */
export function BottomSheetBack(_props: BottomSheetBackProps): null {
  return null;
}
BottomSheetBack.displayName = 'BottomSheetBack';
BottomSheetBack.bottomSheetSlot = 'back' as const;

export interface BottomSheetCloseProps {
  /** Defaults to dismissing the sheet. */
  onPress?: (event?: PressEvent) => void;
  /** Default `'Close'`. */
  accessibilityLabel?: string;
  /**
   * Use `children` as the close control instead of the DS ✕. The child is
   * cloned with `onPress` wired to the dismiss — unless it already has one.
   * Without this flag `children` are ignored and the standard button renders.
   */
  asChild?: boolean;
  /** The custom control. Only rendered with {@link asChild}. */
  children?: ReactNode;
}

/**
 * The close control, in the header's trailing slot.
 *
 * Bare (`<BottomSheetClose />`) it is the standard DS ✕ — Button `tertiary`
 * `s` (36px ghost), or `secondary` over an image header where a ghost glyph
 * would vanish — just positioned by the header rather than floated at the
 * card's corner the way `closable` puts it.
 *
 * `<BottomSheetClose asChild>` swaps in your own control:
 *
 * ```tsx
 * <BottomSheetClose asChild>
 *   <Button variant="tertiary" size="s" icon={ChevronDown} />
 * </BottomSheetClose>
 * ```
 */
export function BottomSheetClose(_props: BottomSheetCloseProps): null {
  return null;
}
BottomSheetClose.displayName = 'BottomSheetClose';
BottomSheetClose.bottomSheetSlot = 'close' as const;

/** Content-surface fill. `'dotted'` is Figma's dot-grid background. */
export type BottomSheetContentVariant = 'default' | 'dotted';

export interface BottomSheetContentProps {
  /** The sheet's main content. */
  children?: ReactNode;
  /**
   * `'default'` — the translucent `secondary-30` card.
   * `'dotted'` — Figma's flat `secondary-50` base with the dot grid drawn on
   * top (5662:11360). Use it for content that is itself artwork or a preview,
   * where the dots read as a canvas rather than a panel.
   */
  variant?: BottomSheetContentVariant;
  /** Extra classes on the body wrapper (same target as `contentClassName`). */
  className?: string;
}

/**
 * Figma's "Media Slot" (5443:44621) — the sheet's content SURFACE: a
 * translucent `secondary-50/70` card with a 1px `secondary-200` border, a 28px
 * radius, clipped contents and 12px of internal padding. 5500:87000 shows it
 * holding a three-dot menu, which is what most option sheets are.
 *
 * This is not a pass-through: wrapping content in it CHANGES how the sheet
 * looks, and that surface is the design. Leave it off for full-bleed bodies
 * (PDF, WebView, image viewer) that must reach the card's edges.
 *
 * Body PADDING outside the surface stays a sheet-level prop (`padding`): the
 * shells resolve it together with the safe-area and footer clearance before the
 * body is even built, so it can't be decided from down here.
 *
 * With a `steps` sheet the body is the step slider, not `children`, so the
 * marker goes in EMPTY — `<BottomSheetContent />` — and only asks for the
 * surface to be drawn around the slider. Children passed alongside `steps` are
 * ignored, same as any other child would be.
 */
export function BottomSheetContent(_props: BottomSheetContentProps): null {
  return null;
}
BottomSheetContent.displayName = 'BottomSheetContent';
BottomSheetContent.bottomSheetSlot = 'content' as const;

export interface BottomSheetActionsProps {
  /**
   * `'stacked'` (default) — Figma "Double button Horixontal aligned": a column
   * of full-width buttons. `'inline'` — "Double button vertically aligned": a
   * row of half-width ones. Write the children in the order Figma shows them:
   * primary first when stacked, secondary first when inline.
   */
  layout?: BottomSheetFooterLayout;
  /**
   * Float the footer over the body instead of sitting in flow (Figma
   * `Overlayered …`). Native always floats it — gorhom owns that slot.
   */
  overlay?: boolean;
  /**
   * Force the slight body-under-footer dissolve (the body's tail runs
   * `FOOTER_OVERLAP_PX` under the blurred footer) even when the sheet hugs
   * its content. By default the dissolve only happens when the sheet is
   * height-constrained; a content-sized sheet stacks body and footer with no
   * overlap. Opt in for compositions where the artwork is MEANT to bleed
   * under the actions (Figma 5662:11360, the notification prompt).
   */
  overlap?: boolean;
  /** Footer buttons. Shared `<Button>` children get the DS footer sizing. */
  children?: ReactNode;
}

/**
 * The footer region. Its `<Button>` children are given Figma's footer sizing
 * (`xxl`, full-width when stacked, `flex-1` when inline) unless they set those
 * props themselves, so a footer can't drift from the spec by omission.
 */
export function BottomSheetActions(_props: BottomSheetActionsProps): null {
  return null;
}
BottomSheetActions.displayName = 'BottomSheetActions';
BottomSheetActions.bottomSheetSlot = 'actions' as const;

// ─── Split ──────────────────────────────────────────────────────────────────

/** What {@link splitBottomSheetChildren} pulls out of a sheet's children. */
export interface BottomSheetSlots {
  header?: BottomSheetHeaderProps;
  title?: BottomSheetTitleProps;
  description?: BottomSheetDescriptionProps;
  image?: BottomSheetImageProps;
  back?: BottomSheetBackProps;
  close?: BottomSheetCloseProps;
  content?: BottomSheetContentProps;
  actions?: BottomSheetActionsProps;
  /**
   * The Figma "Content" region: `<BottomSheetContent>`'s children plus any
   * non-slot children left at the top level. Concatenated rather than either/or
   * so mixing the two spellings can never silently drop content.
   */
  body: ReactNode;
  /**
   * Just the non-slot children. When a `<BottomSheetContent>` is present the
   * Surface draws its bordered card around the SLOT's children only and
   * renders these loose siblings after it — body content below the surface
   * (e.g. the auth sheet's CTAs under the fields card).
   */
  loose: ReactNode;
}

/**
 * Route a sheet's children into regions. Pure, cheap, and called by BOTH the
 * Surface and the native shell — the shell needs the footer before it can hand
 * it to gorhom, the Surface needs the rest.
 */
export function splitBottomSheetChildren(children: ReactNode): BottomSheetSlots {
  const slots: BottomSheetSlots = { body: null, loose: null };

  const collect = (nodes: ReactNode) => {
    Children.forEach(nodes, (child) => {
      const slot = slotOf(child);
      if (!slot) return;
      const element = child as ReactElement<Record<string, unknown>>;
      switch (slot) {
        case 'header':
          slots.header = element.props as BottomSheetHeaderProps;
          collect((element.props as BottomSheetHeaderProps).children);
          break;
        case 'title':
          slots.title = element.props as BottomSheetTitleProps;
          break;
        case 'description':
          slots.description = element.props as BottomSheetDescriptionProps;
          break;
        case 'image':
          slots.image = element.props as BottomSheetImageProps;
          break;
        case 'back':
          slots.back = element.props as BottomSheetBackProps;
          break;
        case 'close':
          slots.close = element.props as BottomSheetCloseProps;
          break;
        case 'content':
          slots.content = element.props as BottomSheetContentProps;
          break;
        case 'actions':
          slots.actions = element.props as BottomSheetActionsProps;
          break;
      }
    });
  };

  collect(children);
  // `Children.map` (not a filtered array) so the surviving body children keep
  // the keys React assigned them — dropping a slot must not reshuffle the rest.
  const loose = Children.map(children, (child) => (slotOf(child) ? null : child));
  slots.loose = loose;
  slots.body = slots.content
    ? [
        <Fragment key="bs-content">{slots.content.children}</Fragment>,
        <Fragment key="bs-loose">{loose}</Fragment>,
      ]
    : loose;
  return slots;
}
